import { useEffect, useRef, type MutableRefObject } from 'react'
import { cn } from '@/lib/utils'
import type { TeamPerson } from './api'
import type { Pos, World, ZoneKind } from './arena'

export const ZONE_STYLE: Record<ZoneKind, string> = {
  pod: 'border-border-soft bg-surface/70',
  meeting: 'border-info/30 bg-info-soft/50',
  kitchen: 'border-warning/30 bg-warning-soft/60',
  lounge: 'border-success/30 bg-success-soft/60',
  hall: 'border-transparent',
}

/** The whole floor at a glance: you and whoever is out walking. Display only; it reads where you stand a few times a second. */
export function Minimap({ world, roaming, posRef, className }: { world: World; roaming: readonly TeamPerson[]; posRef: MutableRefObject<Pos | null>; className?: string }) {
  const dot = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const place = () => {
      const p = posRef.current
      if (dot.current && p) dot.current.style.transform = `translate(${(p.x / world.w) * 100}cqw, ${(p.y / world.h) * 100}cqh)`
    }
    place()
    const id = window.setInterval(place, 250)
    return () => window.clearInterval(id)
  }, [world, posRef])
  return (
    <div className={cn('rounded-lg border border-border bg-card/90 p-1.5 shadow-sm', className)} aria-hidden>
      <div className="relative w-full [container-type:size]" style={{ aspectRatio: `${world.w} / ${world.h}` }}>
        {world.zones
          .filter((z) => z.kind !== 'hall')
          .map((z) => (
            <span key={z.id} className={cn('absolute rounded-[2px] border', ZONE_STYLE[z.kind])} style={{ left: `${(z.x / world.w) * 100}%`, top: `${(z.y / world.h) * 100}%`, width: `${(z.w / world.w) * 100}%`, height: `${(z.h / world.h) * 100}%` }} />
          ))}
        {roaming.map((p) => (
          <span key={p.userId} className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-success" style={{ left: `${((p.pos?.x ?? 0) / world.w) * 100}%`, top: `${((p.pos?.y ?? 0) / world.h) * 100}%` }} />
        ))}
        <span ref={dot} className="absolute left-0 top-0 -ml-1 -mt-1 size-2 rounded-full bg-brand ring-2 ring-brand/30" />
      </div>
    </div>
  )
}
